import React, { useState } from 'react';
import { Download, FileText, Code, Trash2, CheckCircle } from 'lucide-react';
import type { RAGDocument } from '@/types/rag';
import { useUser } from '@/contexts/UserContext';

interface CardDetailActionsProps {
    doc: RAGDocument;
    onView: (doc: RAGDocument) => void;
    onDelete: (id: string) => void;
}

type ActionVariant = 'emerald' | 'cyan' | 'violet' | 'rose';

interface ActionButtonProps {
    icon: React.ElementType;
    label: string;
    variant: ActionVariant;
    onClick: (e: React.MouseEvent) => void;
    disabled?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({ icon: Icon, label, variant, onClick, disabled }) => {
    // Simplified, static-neon style
    const variants = {
        emerald: {
            container: "border-emerald-500/50 from-emerald-950/90 to-black text-emerald-400 hover:border-emerald-400 hover:bg-emerald-950/50 hover:text-emerald-300",
            glow: "shadow-[inset_0_0_20px_rgba(16,185,129,0.15)] hover:shadow-[inset_0_0_30px_rgba(16,185,129,0.25)]",
            iconColor: "drop-shadow-[0_0_5px_rgba(16,185,129,0.5)]"
        },
        cyan: {
            container: "border-cyan-500/50 from-cyan-950/90 to-black text-cyan-400 hover:border-cyan-400 hover:bg-cyan-950/50 hover:text-cyan-300",
            glow: "shadow-[inset_0_0_20px_rgba(34,211,238,0.15)] hover:shadow-[inset_0_0_30px_rgba(34,211,238,0.25)]",
            iconColor: "drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]"
        },
        violet: {
            container: "border-violet-500/50 from-violet-950/90 to-black text-violet-400 hover:border-violet-400 hover:bg-violet-950/50 hover:text-violet-300",
            glow: "shadow-[inset_0_0_20px_rgba(139,92,246,0.15)] hover:shadow-[inset_0_0_30px_rgba(139,92,246,0.25)]",
            iconColor: "drop-shadow-[0_0_5px_rgba(139,92,246,0.5)]"
        },
        rose: {
            container: "border-rose-500/50 from-rose-950/90 to-black text-rose-400 hover:border-rose-400 hover:bg-rose-950/50 hover:text-rose-300",
            glow: "shadow-[inset_0_0_20px_rgba(244,63,94,0.15)] hover:shadow-[inset_0_0_30px_rgba(244,63,94,0.25)]",
            iconColor: "drop-shadow-[0_0_5px_rgba(244,63,94,0.5)]"
        }
    };

    const currentVariant = variants[variant];

    return (
        <button
            onClick={(e) => {
                e.stopPropagation();
                onClick(e);
            }}
            disabled={disabled}
            className={`
                group relative flex flex-col items-center justify-center p-1 
                aspect-square w-full rounded-xl border
                bg-gradient-to-b backdrop-blur-md
                ${currentVariant.container}
                ${currentVariant.glow}
                disabled:opacity-50 disabled:cursor-not-allowed
            `}
        >
            {/* Top Specular Highlight */}
            <div className="absolute inset-x-2 top-0 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent" />

            <div className="flex-1 flex flex-col items-center justify-center relative w-full min-h-0">
                {/* Icon Container */}
                <div className="relative z-10 p-1">
                    <Icon
                        className={`w-5 h-5 sm:w-6 sm:h-6 ${currentVariant.iconColor}`}
                        strokeWidth={2}
                    />
                </div>
            </div>

            <div className="w-full flex items-center justify-center pb-1.5">
                <span className="text-[8px] sm:text-[9px] font-bold tracking-tight uppercase opacity-90 group-hover:opacity-100 text-center leading-none">
                    {label}
                </span>
            </div>
        </button>
    );
};

export const CardDetailActions: React.FC<CardDetailActionsProps> = ({ doc, onView, onDelete }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const { session } = useUser();

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const token = session?.access_token;
            if (!token) {
                console.error("No auth token available for download");
                setIsDownloading(false);
                return;
            }

            const apiUrl = import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000';
            // Assuming the endpoint structure based on knowledge.py
            const response = await fetch(`${apiUrl}/api/knowledge/documents/${doc.id}/content`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Download failed: ${response.statusText}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error("Error downloading document:", error);
            // In a real app, show a toast here
        } finally {
            setIsDownloading(false);
        }
    };

    const handleExportJson = () => {
        const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(
            JSON.stringify(doc, null, 2)
        )}`;
        const link = document.createElement("a");
        link.href = jsonString;
        link.download = `${doc.filename}_meta.json`;
        link.click();
    };

    return (
        <div className="w-full mt-4">
            <div className="grid grid-cols-4 gap-2">
                <ActionButton
                    icon={isDownloading ? CheckCircle : Download}
                    label={isDownloading ? "READY" : "DOWNLOAD"}
                    variant="emerald"
                    onClick={handleDownload}
                    disabled={isDownloading}
                />
                <ActionButton
                    icon={FileText}
                    label="CHUNKS"
                    variant="cyan"
                    onClick={() => onView(doc)}
                />
                <ActionButton
                    icon={Code}
                    label="EXPORT"
                    variant="violet"
                    onClick={handleExportJson}
                />
                <ActionButton
                    icon={Trash2}
                    label="DELETE"
                    variant="rose"
                    onClick={() => onDelete(doc.id)}
                />
            </div>
        </div>
    );
};
